import axios from 'axios';
import config from '../config';
import {
  CompanyDocumentType,
  PartnerDocumentType,
  IndividualDocumentType,
  DocumentOwnerType,
  DocumentStatus,
  GetDocumentsByCompanyResult,
  GetDocumentsByPartnerResult,
} from '../types/documents';

const { api: apiConfig } = config;

const documentsAxios = axios.create({
  baseURL: `${apiConfig.API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
documentsAxios.interceptors.request.use(
  (config) => {
    const authUser = sessionStorage.getItem('authUser');
    if (authUser) {
      const user = JSON.parse(authUser);
      if (user && user.token) {
        config.headers.Authorization = `Bearer ${user.token}`;
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor
documentsAxios.interceptors.response.use(
  (response) => response,
  (error) => {
    return Promise.reject(error);
  }
);

export interface UploadCompanyDocumentParams {
  companyId: string;
  file: File;
  documentType: CompanyDocumentType;
  expirationDate?: string;
  notes?: string;
  onProgress?: (progress: number) => void;
}

export interface UploadPartnerDocumentParams {
  partnerId: string;
  companyId: string;
  file: File;
  documentType: PartnerDocumentType;
  expirationDate?: string;
  notes?: string;
  onProgress?: (progress: number) => void;
}

export interface UploadIndividualDocumentParams {
  individualId: string;
  file: File;
  documentType: IndividualDocumentType;
  expirationDate?: string;
  notes?: string;
  onProgress?: (progress: number) => void;
}

export interface ReplaceDocumentParams {
  documentId: string;
  ownerType: DocumentOwnerType;
  file: File;
  expirationDate?: string;
  notes?: string;
  onProgress?: (progress: number) => void;
}

export interface DeleteDocumentParams {
  documentId: string;
  ownerType: DocumentOwnerType;
  deleteReason?: string;
}

export interface RestoreDocumentParams {
  documentId: string;
  ownerType: DocumentOwnerType;
  restoreReason?: string;
}

export interface UpdateDocumentTypeParams {
  documentId: string;
  ownerType: DocumentOwnerType;
  newDocumentType: CompanyDocumentType | PartnerDocumentType | IndividualDocumentType;
}

export interface UpdateDocumentFileNameParams {
  documentId: string;
  ownerType: DocumentOwnerType;
  newFileName: string;
}

export interface BatchUploadDocumentsParams {
  uploadSessionId: string;
  ownerType: DocumentOwnerType;
  ownerId: string;
  companyId?: string;
  files: Array<{
    file: File;
    documentType: CompanyDocumentType | PartnerDocumentType | IndividualDocumentType;
    startDate?: string;
    expirationDate?: string;
    notes?: string;
  }>;
}

export interface BatchUploadDocumentsResult {
  uploadSessionId: string;
  totalFiles: number;
  successfulUploads: number;
  failedUploads: number;
  uploadedFiles: Array<{
    documentId: string;
    fileName: string;
    documentType: string;
    fileSize: number;
    r2Key: string;
  }>;
  failures: Array<{
    fileName: string;
    error: string;
    stackTrace?: string;
  }>;
}

class DocumentsService {
  /**
   * Upload a document for a company
   */
  async uploadCompanyDocument(
    params: UploadCompanyDocumentParams
  ): Promise<any> {
    const formData = new FormData();
    // Explicitly pass the filename as the third parameter to ensure it's used
    formData.append('file', params.file, params.file.name);

    const config = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      params: {
        documentType: params.documentType,
        expirationDate: params.expirationDate,
        notes: params.notes,
      },
      onUploadProgress: (progressEvent: any) => {
        if (params.onProgress && progressEvent.total) {
          const progress = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          params.onProgress(progress);
        }
      },
    };

    const response = await documentsAxios.post(
      `/documents/company/${params.companyId}`,
      formData,
      config
    );

    return response.data;
  }

  /**
   * Upload a document for a partner
   */
  async uploadPartnerDocument(
    params: UploadPartnerDocumentParams
  ): Promise<any> {
    const formData = new FormData();
    // Explicitly pass the filename as the third parameter to ensure it's used
    formData.append('file', params.file, params.file.name);

    const config = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      params: {
        companyId: params.companyId,
        documentType: params.documentType,
        expirationDate: params.expirationDate,
        notes: params.notes,
      },
      onUploadProgress: (progressEvent: any) => {
        if (params.onProgress && progressEvent.total) {
          const progress = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          params.onProgress(progress);
        }
      },
    };

    const response = await documentsAxios.post(
      `/documents/partner/${params.partnerId}`,
      formData,
      config
    );

    return response.data;
  }

  /**
   * Get all documents for a company with optional filtering
   */
  async getCompanyDocuments(
    companyId: string,
    documentType?: CompanyDocumentType,
    status?: DocumentStatus,
    includeDeleted: boolean = false
  ): Promise<GetDocumentsByCompanyResult> {
    const params: any = {};

    if (documentType !== undefined) params.documentType = documentType;
    if (status !== undefined) params.status = status;
    if (includeDeleted) params.includeDeleted = includeDeleted;

    const response = await documentsAxios.get(`/documents/company/${companyId}`, {
      params,
    });

    return response.data;
  }

  /**
   * Get all documents for a partner with optional filtering
   */
  async getPartnerDocuments(
    partnerId: string,
    documentType?: PartnerDocumentType,
    status?: DocumentStatus,
    includeDeleted: boolean = false
  ): Promise<GetDocumentsByPartnerResult> {
    const params: any = {};

    if (documentType !== undefined) params.documentType = documentType;
    if (status !== undefined) params.status = status;
    if (includeDeleted) params.includeDeleted = includeDeleted;

    const response = await documentsAxios.get(`/documents/partner/${partnerId}`, {
      params,
    });

    return response.data;
  }

  /**
   * Upload a document for an individual
   */
  async uploadIndividualDocument(
    params: UploadIndividualDocumentParams
  ): Promise<any> {
    const formData = new FormData();
    // Explicitly pass the filename as the third parameter to ensure it's used
    formData.append('file', params.file, params.file.name);

    const config = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      params: {
        documentType: params.documentType,
        expirationDate: params.expirationDate,
        notes: params.notes,
      },
      onUploadProgress: (progressEvent: any) => {
        if (params.onProgress && progressEvent.total) {
          const progress = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          params.onProgress(progress);
        }
      },
    };

    const response = await documentsAxios.post(
      `/documents/individual/${params.individualId}`,
      formData,
      config
    );

    return response.data;
  }

  /**
   * Get all documents for an individual with optional filtering
   */
  async getIndividualDocuments(
    individualId: string,
    documentType?: IndividualDocumentType,
    status?: DocumentStatus,
    includeDeleted: boolean = false
  ): Promise<any> {
    const params: any = {};

    if (documentType !== undefined) params.documentType = documentType;
    if (status !== undefined) params.status = status;
    if (includeDeleted) params.includeDeleted = includeDeleted;

    const response = await documentsAxios.get(`/documents/individual/${individualId}`, {
      params,
    });

    return response.data;
  }

  /**
   * Replace an existing document with a new version
   * Admin only
   */
  async replaceDocument(params: ReplaceDocumentParams): Promise<any> {
    const formData = new FormData();
    // Explicitly pass the filename as the third parameter to ensure it's used
    formData.append('file', params.file, params.file.name);

    const config = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      params: {
        ownerType: params.ownerType,
        expirationDate: params.expirationDate,
        notes: params.notes,
      },
      onUploadProgress: (progressEvent: any) => {
        if (params.onProgress && progressEvent.total) {
          const progress = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          params.onProgress(progress);
        }
      },
    };

    const response = await documentsAxios.put(
      `/documents/${params.documentId}/replace`,
      formData,
      config
    );

    return response.data;
  }

  /**
   * Soft delete a document (moves to Delete folder in R2)
   * Admin only
   */
  async deleteDocument(params: DeleteDocumentParams): Promise<any> {
    const config = {
      params: {
        ownerType: params.ownerType,
        deleteReason: params.deleteReason,
      },
    };

    const response = await documentsAxios.delete(
      `/documents/${params.documentId}`,
      config
    );

    return response.data;
  }

  /**
   * Restore a soft-deleted document from Delete folder
   * Admin only
   */
  async restoreDocument(params: RestoreDocumentParams): Promise<any> {
    const config = {
      params: {
        ownerType: params.ownerType,
        restoreReason: params.restoreReason,
      },
    };

    const response = await documentsAxios.post(
      `/documents/${params.documentId}/restore`,
      null,
      config
    );

    return response.data;
  }

  /**
   * Get audit log history for documents
   * Admin only
   */
  async getAuditLog(filters?: {
    documentId?: string;
    companyId?: string;
    partnerId?: string;
    action?: number;
    startDate?: string;
    endDate?: string;
    limit?: number;
  }): Promise<any> {
    const response = await documentsAxios.get('/documents/audit-log', {
      params: filters,
    });

    return response.data;
  }

  /**
   * Download a document using signed URL
   */
  async downloadDocument(signedUrl: string, fileName: string): Promise<void> {
    try {
      // Fetch the file content
      const response = await fetch(signedUrl);

      if (!response.ok) {
        throw new Error('Download failed');
      }

      // Get the blob
      const blob = await response.blob();

      // Create a blob URL and trigger download
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Cleanup blob URL
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Download error:', error);
      // Fallback: open in new tab if fetch fails
      window.open(signedUrl, '_blank');
    }
  }

  /**
   * Preview a document in new tab using signed URL
   */
  async previewDocument(signedUrl: string): Promise<void> {
    window.open(signedUrl, '_blank');
  }

  /**
   * Update document type (reclassify document)
   * Moves the file to new folder in R2 storage
   * Admin only
   */
  async updateDocumentType(params: UpdateDocumentTypeParams): Promise<any> {
    const config = {
      params: {
        ownerType: params.ownerType,
        newDocumentType: params.newDocumentType,
      },
    };

    const response = await documentsAxios.patch(
      `/documents/${params.documentId}/type`,
      null,
      config
    );

    return response.data;
  }

  /**
   * Update the original filename of a document
   * Useful when Windows provides 8.3 short filenames (e.g., CARTOD~1.PDF)
   */
  async updateDocumentFileName(params: UpdateDocumentFileNameParams): Promise<any> {
    const config = {
      params: {
        ownerType: params.ownerType,
        newFileName: params.newFileName,
      },
    };

    const response = await documentsAxios.patch(
      `/documents/${params.documentId}/filename`,
      null,
      config
    );

    return response.data;
  }

  /**
   * Upload multiple documents in a single batch operation.
   * Uses SignalR for real-time progress updates.
   * Connect to DocumentUploadHub and join the uploadSessionId before calling this.
   */
  async batchUploadDocuments(
    params: BatchUploadDocumentsParams
  ): Promise<BatchUploadDocumentsResult> {
    const formData = new FormData();

    // Add all files
    params.files.forEach((fileItem) => {
      formData.append('files', fileItem.file, fileItem.file.name);
    });

    // Add document types as comma-separated values
    const documentTypes = params.files.map((f) => f.documentType).join(',');
    formData.append('documentTypes', documentTypes);

    // Add optional start dates as comma-separated ISO strings
    const startDates = params.files
      .map((f) => f.startDate || '')
      .join(',');
    if (startDates) {
      formData.append('startDates', startDates);
    }

    // Add optional expiration dates as comma-separated ISO strings
    const expirationDates = params.files
      .map((f) => f.expirationDate || '')
      .join(',');
    if (expirationDates) {
      formData.append('expirationDates', expirationDates);
    }

    // Add optional notes as comma-separated values
    const notes = params.files.map((f) => f.notes || '').join(',');
    if (notes) {
      formData.append('notes', notes);
    }

    const config = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      params: {
        uploadSessionId: params.uploadSessionId,
        ownerType: params.ownerType,
        ownerId: params.ownerId,
        companyId: params.companyId,
      },
    };

    const response = await documentsAxios.post('/documents/batch', formData, config);

    return response.data;
  }
}

export const documentsService = new DocumentsService();
